/**
 * IPD Timeline Print Utility
 * Opens a formatted print view in a new window, oldest entries first.
 */

const fmt = (date: string | null | undefined, includeTime = true): string => {
  if (!date) return "—";
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
    });
  } catch {
    return date;
  }
};

const esc = (s: any): string =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const draftJsToText = (raw: any): string => {
  if (!raw) return "";
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed?.blocks)) {
      return parsed.blocks
        .map((b: any) => b.text ?? "")
        .filter(Boolean)
        .join("\n");
    }
  } catch {}
  return typeof raw === "string" ? raw : "";
};

const field = (label: string, value: any): string => {
  if (value === null || value === undefined || value === "" || value === "—") return "";
  return `<div class="field"><div class="fl">${esc(label)}</div><div class="fv">${esc(String(value))}</div></div>`;
};

const section = (title: string, body: string): string =>
  body.trim() ? `<div class="section"><div class="section-title">${esc(title)}</div>${body}</div>` : "";

const grid = (cols: number, ...items: string[]): string => {
  const filled = items.filter(Boolean);
  if (!filled.length) return "";
  return `<div class="grid-${cols}">${filled.join("")}</div>`;
};

// ── Entry type renderers ────────────────────────────────────────────────────

function renderAdmission(d: any): string {
  const parts: string[] = [];
  const g = grid(
    3,
    field("Admission Date", fmt(d.admissionDate ?? d.admission_date, false)),
    field("Room", d.roomName ?? d.room_name),
    field("Bed", d.bedName ?? d.bed_name),
    field("Chief Complaint", d.chiefComplaint ?? d.chief_complaint),
    field("Treating Doctor", d.doctorName ?? d.doctor_name),
    field("Ward", d.wardName ?? d.ward_name),
  );
  if (g) parts.push(g);
  if (d.notes) parts.push(`<div class="notes">${esc(d.notes)}</div>`);
  return parts.join("");
}

function renderDoctorRounds(d: any): string {
  const parts: string[] = [];

  const vitals = d.vitals ?? {};
  const vitalItems = [
    { label: "BP", value: vitals.bp, unit: "mmHg" },
    { label: "Pulse", value: vitals.pulse, unit: "bpm" },
    { label: "Temp", value: vitals.temp, unit: "°C" },
    { label: "SpO2", value: vitals.spo2, unit: "%" },
  ].filter((v) => v.value);

  if (vitalItems.length) {
    parts.push(
      `<div class="vitals-row">${vitalItems
        .map(
          (v) =>
            `<span class="vital"><span class="vl">${esc(v.label)}</span> <strong>${esc(String(v.value))}</strong> <span class="vu">${esc(v.unit)}</span></span>`
        )
        .join("")}</div>`
    );
  }

  const obs = d.observations ?? "";
  const plan = d.plan ?? "";
  if (obs || plan) {
    parts.push(
      grid(
        2,
        obs ? `<div class="field"><div class="fl">Observations</div><div class="fv pre">${esc(obs)}</div></div>` : "",
        plan ? `<div class="field"><div class="fl">Plan</div><div class="fv pre">${esc(plan)}</div></div>` : ""
      )
    );
  }

  const meds: any[] = Array.isArray(d.medicationsUpdated) ? d.medicationsUpdated : [];
  if (meds.length) {
    const medRows = meds
      .map((m: any) => {
        const action = m.action ?? "updated";
        const cls =
          action === "added" ? "badge-added" : action === "stopped" ? "badge-stopped" : "badge-updated";
        const dose = m.instructions?.dose ?? m.instructions?.power ?? "";
        const route = m.instructions?.route ?? "";
        const freq = m.instructions?.frequency ?? "";
        const detail = [dose, route, freq].filter(Boolean).join(" · ");
        return `<div class="med-row"><span class="badge ${cls}">${esc(action)}</span> <strong>${esc(m.medicineName ?? m.name ?? "")}</strong>${detail ? ` <span class="dim">· ${esc(detail)}</span>` : ""}</div>`;
      })
      .join("");
    parts.push(section("Medications", medRows));
  }

  if (d.notes) parts.push(`<div class="notes">${esc(d.notes)}</div>`);
  return parts.join("");
}

function renderNursing(d: any): string {
  const parts: string[] = [];

  const shift = d.shift
    ? { morning: "Morning (6am–2pm)", evening: "Evening (2pm–10pm)", night: "Night (10pm–6am)" }[d.shift as string] ?? d.shift
    : null;

  const vitals = d.vitals ?? {};
  const vitalItems = [
    { label: "BP", value: vitals.bp, unit: "mmHg" },
    { label: "Pulse", value: vitals.pulse, unit: "bpm" },
    { label: "Temp", value: vitals.temp, unit: "°C" },
    { label: "SpO2", value: vitals.spo2, unit: "%" },
  ].filter((v) => v.value);

  if (shift || vitalItems.length) {
    const meta = [];
    if (shift) meta.push(`<strong>${esc(shift)}</strong>`);
    if (vitalItems.length) {
      meta.push(
        vitalItems
          .map(
            (v) =>
              `<span class="vital"><span class="vl">${esc(v.label)}</span> <strong>${esc(String(v.value))}</strong> <span class="vu">${esc(v.unit)}</span></span>`
          )
          .join(" ")
      );
    }
    parts.push(`<div class="vitals-row">${meta.join(" &nbsp;·&nbsp; ")}</div>`);
  }

  if (d.notes) parts.push(`<div class="notes pre">${esc(d.notes)}</div>`);

  const meds: any[] = Array.isArray(d.medicationsAdministered) ? d.medicationsAdministered : [];
  const given = meds.filter((m) => m.given);
  const notGiven = meds.filter((m) => !m.given);

  if (given.length) {
    parts.push(
      section(
        "Medications Given",
        `<ul>${given
          .map((m: any) => `<li>${esc(m.medicineName ?? m.name ?? "")}${m.dose ? ` — ${esc(m.dose)}` : ""}</li>`)
          .join("")}</ul>`
      )
    );
  }
  if (notGiven.length) {
    parts.push(
      section(
        "Medications Not Given",
        `<ul>${notGiven
          .map((m: any) => `<li>${esc(m.medicineName ?? m.name ?? "")}${m.reason ? ` — ${esc(m.reason)}` : ""}</li>`)
          .join("")}</ul>`
      )
    );
  }

  return parts.join("");
}

function renderPharmacy(d: any): string {
  const req = d.pharmacy_request ?? d.pharmacyRequest ?? {};
  const saleNumber = req.sale_number ?? req.saleNumber ?? d.pharmacy_sale_id ?? "";
  const status = req.status ?? d.status ?? "";
  const delivery = req.delivery_status ?? req.deliveryStatus ?? d.delivery_status ?? "";
  const items: any[] = req.items ?? [];

  const parts: string[] = [];
  parts.push(
    grid(
      3,
      field("Sale #", saleNumber),
      field("Status", status),
      field("Delivery", delivery.replace(/_/g, " "))
    )
  );

  if (items.length) {
    parts.push(
      section(
        "Items",
        `<ul>${items
          .map((item: any) => {
            const pi = item.pharmacy_item ?? item.pharmacyItem ?? {};
            return `<li>${esc(item.quantity)} × ${esc(pi.name ?? "Unknown")}${pi.sku ? ` (${esc(pi.sku)})` : ""}</li>`;
          })
          .join("")}</ul>`
      )
    );
  }
  if (d.notes) parts.push(`<div class="notes">${esc(d.notes)}</div>`);
  return parts.join("");
}

function renderLabRequest(d: any): string {
  const req = d.lab_request ?? d.labRequest ?? {};
  const labTest = req.lab_test ?? req.labTest ?? {};
  const parts: string[] = [];

  parts.push(
    grid(
      3,
      field("Order #", req.order_number ?? req.orderNumber),
      field("Sample ID", req.sample_id ?? req.sampleId),
      field("Status", req.status),
      field("Priority", req.priority),
      field("Charge", req.charge != null ? `₹${req.charge}` : null),
      field("Referred By", req.referred_by ?? req.referredBy)
    )
  );

  const testName = labTest.name;
  const testCode = labTest.code ? ` (${labTest.code})` : "";
  if (testName) parts.push(section("Lab Test", `<div class="fv"><strong>${esc(testName)}${esc(testCode)}</strong></div>`));
  if (d.notes ?? req.notes) parts.push(`<div class="notes">${esc(d.notes ?? req.notes)}</div>`);
  return parts.join("");
}

function renderOT(d: any): string {
  const parts: string[] = [];
  const procedures: any[] =
    Array.isArray(d.procedures) && d.procedures.length ? d.procedures : d.procedureName ? [{ name: d.procedureName }] : [];

  const dateRaw = d.procedureDate ?? d.procedure_date;
  let dateStr = "—";
  if (dateRaw) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateRaw)) {
      const [y, m, day] = dateRaw.split("-").map(Number);
      dateStr = new Date(y, m - 1, day).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } else {
      dateStr = fmt(dateRaw, false);
    }
  }

  parts.push(
    grid(
      3,
      procedures.length
        ? `<div class="field"><div class="fl">Procedure(s)</div><div class="fv">${procedures.map((p: any) => `<strong>${esc(p.name)}</strong>`).join(", ")}</div></div>`
        : "",
      field("Date", dateStr + (d.procedureTime ?? d.procedure_time ? ` at ${d.procedureTime ?? d.procedure_time}` : "")),
      field("Status", d.status)
    )
  );

  const team = [
    { label: "Surgeon 1", value: d.surgeon1Name ?? d.surgeon1_name },
    { label: "Surgeon 2", value: d.surgeon2Name ?? d.surgeon2_name },
    { label: "Anaesthetist", value: d.anaesthetistName ?? d.anaesthetist_name },
    { label: "Assistant", value: d.assistantName ?? d.assistant_name },
  ].filter((t) => t.value);
  if (team.length) {
    parts.push(section("Surgical Team", grid(4, ...team.map((t) => field(t.label, t.value)))));
  }

  const opDetails = draftJsToText(d.operationDetails ?? d.operation_details);
  if (opDetails) parts.push(section("Operation Details", `<div class="notes pre">${esc(opDetails)}</div>`));

  parts.push(
    grid(
      3,
      field("Findings", d.findings),
      field("Blood Loss", d.bloodLossMl != null ? `${d.bloodLossMl} ml` : d.blood_loss_ml != null ? `${d.blood_loss_ml} ml` : null),
      field("Complications", d.complications),
      field("Implants", d.implants),
      field("Notes", d.notes)
    )
  );

  return parts.join("");
}

function renderRoomTransfer(d: any): string {
  const from = d.from_room_name ?? d.fromRoomName ?? "—";
  const to = d.to_room_name ?? d.toRoomName ?? "—";
  const billedRate = d.billed_rate_per_day ?? d.billedRatePerDay;
  const defaultRate = d.room_type_price_per_day ?? d.roomTypePricePerDay;
  const reason = d.reason ?? "";
  const parts: string[] = [];

  parts.push(`<div class="transfer-arrow"><strong>${esc(from)}</strong> &rarr; <strong>${esc(to)}</strong></div>`);

  if (billedRate != null) {
    const mismatch = defaultRate != null && Number(billedRate) !== Number(defaultRate);
    parts.push(
      `<div class="notes">${mismatch ? "⚠ " : ""}Billed rate: ₹${esc(billedRate)}/day${mismatch ? ` (room default: ₹${esc(defaultRate)}/day)` : ""}</div>`
    );
  }
  if (reason) parts.push(`<div class="notes">${esc(reason)}</div>`);
  return parts.join("");
}

function renderDischarge(d: any): string {
  const parts: string[] = [];
  parts.push(
    grid(
      3,
      field("Discharge Date", fmt(d.dischargeDate ?? d.discharge_date, false)),
      field("Discharge Type", d.dischargeType ?? d.discharge_type),
      field("Condition at Discharge", d.conditionAtDischarge ?? d.condition_at_discharge)
    )
  );
  if (d.summary ?? d.dischargeSummary ?? d.discharge_summary) {
    parts.push(section("Discharge Summary", `<div class="notes pre">${esc(d.summary ?? d.dischargeSummary ?? d.discharge_summary)}</div>`));
  }
  if (d.instructions ?? d.followUpInstructions ?? d.follow_up_instructions) {
    parts.push(section("Follow-up Instructions", `<div class="notes pre">${esc(d.instructions ?? d.followUpInstructions ?? d.follow_up_instructions)}</div>`));
  }
  if (d.notes) parts.push(`<div class="notes">${esc(d.notes)}</div>`);
  return parts.join("");
}

function renderPayment(d: any): string {
  return grid(
    3,
    field("Amount", d.amount != null ? `₹${d.amount}` : null),
    field("Mode", d.paymentMode ?? d.payment_mode),
    field("Receipt #", d.receiptNumber ?? d.receipt_number),
    field("Notes", d.notes)
  );
}

function renderGeneralComment(d: any): string {
  const comment = d.comment ?? d.notes ?? d.text ?? "";
  return comment ? `<div class="notes pre">${esc(comment)}</div>` : "";
}

function renderGenericData(d: any): string {
  const skipKeys = new Set(["id", "createdAt", "updatedAt", "created_at", "updated_at"]);
  const entries = Object.entries(d).filter(
    ([k, v]) => !skipKeys.has(k) && v !== null && v !== undefined && v !== ""
  );
  if (!entries.length) return "";
  return grid(
    3,
    ...entries.map(([k, v]) => {
      const label = k.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/\s+/g, " ").trim();
      const display =
        typeof v === "object"
          ? (v as any)?.label ?? (v as any)?.name ?? JSON.stringify(v)
          : String(v);
      return field(label, display);
    })
  );
}

function renderEntryBody(entryType: string, data: any): string {
  switch (entryType) {
    case "admission":         return renderAdmission(data);
    case "doctor_rounds":     return renderDoctorRounds(data);
    case "nursing":           return renderNursing(data);
    case "pharmacy_request":  return renderPharmacy(data);
    case "lab_request":       return renderLabRequest(data);
    case "ot":                return renderOT(data);
    case "room_transfer":     return renderRoomTransfer(data);
    case "discharge":         return renderDischarge(data);
    case "payment":           return renderPayment(data);
    case "general_comment":   return renderGeneralComment(data);
    default:                  return renderGenericData(data);
  }
}

// ── Type labels & accent colours ────────────────────────────────────────────

const TYPE_LABEL: Record<string, string> = {
  assessment:       "Assessment",
  doctor_rounds:    "Doctor Round",
  nursing:          "Nursing Note",
  pharmacy_request: "Pharmacy",
  lab_request:      "Lab Report",
  room_transfer:    "Room Transfer",
  payment:          "Payment",
  general_comment:  "General Comment",
  discharge:        "Discharge",
  ot:               "OT",
  admission:        "Admission",
};

const TYPE_COLOR: Record<string, string> = {
  assessment:       "#3b82f6",
  doctor_rounds:    "#6366f1",
  nursing:          "#14b8a6",
  pharmacy_request: "#a855f7",
  lab_request:      "#eab308",
  room_transfer:    "#f59e0b",
  payment:          "#22c55e",
  general_comment:  "#9ca3af",
  discharge:        "#ef4444",
  ot:               "#f97316",
  admission:        "#10b981",
};

// ── Print styles ─────────────────────────────────────────────────────────────

const PRINT_CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #1a1a1a; background: #fff; padding: 20px; }

  .doc-header { border-bottom: 2px solid #1a1a1a; padding-bottom: 12px; margin-bottom: 20px; }
  .doc-header h1 { font-size: 18px; font-weight: 700; }
  .doc-header .meta { font-size: 11px; color: #555; margin-top: 3px; }
  .doc-header .print-date { font-size: 10px; color: #888; margin-top: 6px; }

  .entry { page-break-inside: avoid; margin-bottom: 14px; }
  .entry-header { display: flex; align-items: center; gap: 6px; border-left: 3px solid var(--accent); padding: 6px 10px; background: #f9f9f9; border-radius: 0 4px 4px 0; }
  .entry-type { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: var(--accent); }
  .entry-meta { font-size: 10px; color: #666; margin-left: auto; }
  .entry-by { font-size: 10px; color: #888; }
  .entry-body { padding: 8px 10px 8px 13px; }

  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 20px; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px 20px; }
  .grid-4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 8px 16px; }

  .field { min-width: 0; }
  .fl { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #888; margin-bottom: 1px; }
  .fv { font-size: 11px; color: #222; }
  .pre { white-space: pre-line; }

  .section { margin-top: 8px; }
  .section-title { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #888; margin-bottom: 4px; }

  .notes { font-size: 11px; color: #444; margin-top: 8px; }
  .vitals-row { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin-bottom: 6px; }
  .vital { display: flex; align-items: baseline; gap: 3px; }
  .vl { font-size: 9px; color: #888; }
  .vu { font-size: 9px; color: #888; }

  ul { padding-left: 14px; margin: 3px 0; }
  li { font-size: 11px; color: #333; line-height: 1.5; }

  .med-row { display: flex; align-items: center; gap: 5px; font-size: 11px; margin: 2px 0; }
  .badge { font-size: 9px; font-weight: 700; text-transform: uppercase; padding: 1px 5px; border-radius: 3px; }
  .badge-added   { background: #d1fae5; color: #065f46; }
  .badge-stopped { background: #fee2e2; color: #991b1b; }
  .badge-updated { background: #f3f4f6; color: #374151; }
  .dim { color: #888; }

  .transfer-arrow { font-size: 12px; }

  @media print {
    @page { size: A4 portrait; margin: 12mm 14mm; }
    body { padding: 0; }
    .entry { page-break-inside: avoid; }
  }
`;

// ── Public function ───────────────────────────────────────────────────────────

export function printIPDTimeline(
  timelineEntries: any[],
  patient: any,
  visit: any
): void {
  const sorted = [...timelineEntries].sort((a, b) => {
    const dA = new Date(a.createdAt ?? a.created_at ?? 0).getTime();
    const dB = new Date(b.createdAt ?? b.created_at ?? 0).getTime();
    return dA - dB; // oldest first
  });

  const patientName = [patient?.firstName, patient?.lastName].filter(Boolean).join(" ") || "Patient";
  const regNo = patient?.registrationNumber ? ` (${patient.registrationNumber})` : "";
  const visitNo = visit?.visitNumber ? `Visit #${visit.visitNumber}` : "";
  const roomName = visit?.room?.name ?? "";
  const doctor = visit?.user
    ? `Dr. ${[visit.user.firstName, visit.user.middleName, visit.user.lastName].filter(Boolean).join(" ")}`
    : "";
  const admDate = visit?.meta?.admissionDate ? `Admitted: ${fmt(visit.meta.admissionDate, false)}` : "";
  const metaParts = [visitNo, roomName, doctor, admDate].filter(Boolean).join("  ·  ");

  const now = new Date();
  const printedAt = `Printed on ${now.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} at ${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;

  const entriesHtml = sorted
    .map((entry) => {
      const type = entry.entryType ?? entry.entry_type ?? "";
      const data = entry.formData ?? entry.form_data ?? {};
      const date = fmt(entry.createdAt ?? entry.created_at);
      const by = entry.createdBy?.user?.name ?? entry.created_by?.user?.name ?? "";
      const label = TYPE_LABEL[type] ?? type;
      const accent = TYPE_COLOR[type] ?? "#6b7280";
      const body = renderEntryBody(type, data);

      return `
        <div class="entry" style="--accent:${accent}">
          <div class="entry-header">
            <span class="entry-type">${esc(label)}</span>
            <span class="entry-meta">${esc(date)}</span>
            ${by ? `<span class="entry-by">· ${esc(by)}</span>` : ""}
          </div>
          ${body ? `<div class="entry-body">${body}</div>` : ""}
        </div>
      `;
    })
    .join("\n");

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>IPD Timeline — ${esc(patientName)}</title>
  <style>${PRINT_CSS}</style>
</head>
<body>
  <div class="doc-header">
    <h1>${esc(patientName)}${esc(regNo)}</h1>
    ${metaParts ? `<div class="meta">${esc(metaParts)}</div>` : ""}
    <div class="print-date">${esc(printedAt)}</div>
  </div>
  ${entriesHtml}
</body>
</html>`;

  const win = window.open("", "_blank", "width=820,height=1000");
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => {
    win.print();
  }, 400);
}
